import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X, Upload } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Owner-only "Set cover" control: paste an https image URL or upload a file. Calls PUT {endpoint}
// and hands the server response to onDone so the UI can update immediately.
export function CoverSetter({ endpoint, onDone, testid = "set-cover" }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const submitUrl = async (e) => {
    stop(e);
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put(endpoint, { image_url: url.trim() });
      toast.success("Cover updated");
      setOpen(false); setUrl("");
      onDone?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.put(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated");
      setOpen(false);
      onDone?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`${testid}-open`}
        onClick={(e) => { stop(e); setOpen(true); }}
        className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-black"
      >
        <ImagePlus className="h-3.5 w-3.5" /> Set cover
      </button>
    );
  }

  return (
    <div
      data-testid={`${testid}-panel`}
      onClick={stop}
      className="w-64 rounded-xl border border-black/10 bg-white p-3 text-left shadow-xl"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-[#111]">Set a real cover</span>
        <button type="button" onClick={(e) => { stop(e); setOpen(false); }} className="text-neutral-400 hover:text-black">
          <X className="h-4 w-4" />
        </button>
      </div>
      <Input
        data-testid={`${testid}-url`}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste image URL (https)"
        className="mb-2 h-8 text-xs"
        disabled={busy}
      />
      <div className="flex gap-2">
        <Button data-testid={`${testid}-save`} onClick={submitUrl} disabled={busy || !url.trim()} className="h-8 flex-1 rounded-full bg-[#35606E] text-xs hover:bg-[#294b57]">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save URL"}
        </Button>
        <Button data-testid={`${testid}-file`} onClick={(e) => { stop(e); fileRef.current?.click(); }} disabled={busy} variant="outline" className="h-8 rounded-full border-neutral-300 text-xs">
          <Upload className="h-3.5 w-3.5" />
        </Button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={submitFile} />
    </div>
  );
}
